import { useAuth } from '@/auth/useAuth';
import { useOrg } from '@/organization/useOrg';
import { useOrganizationPerms } from '@/organization/useOrganizationRole';
import { getCurrentOrganizationUser } from '@/services/userService';
import type { Member } from '@/types/user';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Button,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { EditUserModal } from './components/EditUserModal';
import { InvitationsList } from './components/InvitationsList';
import { InviteUserModal } from './components/InviteUserModal';
import { UsersList } from './components/UsersList';

export function UsersManagementPage() {
  const { selectedOrg } = useOrg();
  const organizationId: string = selectedOrg?.id || '';
  const { data: perms } = useOrganizationPerms(organizationId);
  const canManageUsers = perms?.canManageUsers;
  const { user: _currentUser } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<Member | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const userQuery = useQuery({
    queryKey: ['organization-current-user', organizationId],
    queryFn: () => getCurrentOrganizationUser(organizationId),
  });

  const member: Member | null = userQuery.data ? userQuery.data : null;

  if (!selectedOrg) {
    return (
      <Container>
        <Box sx={{ mb: 3 }} />
        <Paper sx={{ p: 4 }}>
          <Typography variant='body1' color='text.primary' align='center'>
            No organization was selected, please join or select an organization
            to view users.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth='lg'>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant='h4'>User Management</Typography>
        <Typography>{canManageUsers}</Typography>
        {canManageUsers && (
          <Button variant='contained' onClick={() => setInviteOpen(true)}>
            Invite User
          </Button>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          <Tab
            icon={<PersonIcon />}
            iconPosition='start'
            label='Active Users'
          />
          {canManageUsers && (
            <Tab
              icon={<EmailIcon />}
              iconPosition='start'
              label='Invitations'
            />
          )}
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <UsersList
          organizationId={organizationId}
          onEditUser={setEditingUser}
        />
      )}

      {activeTab === 1 && canManageUsers && (
        <InvitationsList organizationId={organizationId} />
      )}
      {canManageUsers && (
        <InviteUserModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          organizationId={organizationId}
        />
      )}
      {editingUser &&
        (canManageUsers ||
          editingUser.userDetails.uuid === member?.userDetails.uuid) && (
          <EditUserModal
            open={!!editingUser}
            user={editingUser}
            organizationId={organizationId}
            onClose={() => setEditingUser(null)}
          />
        )}
    </Container>
  );
}
